import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import Landing from "@/pages/Landing";
import Onboarding from "@/pages/Onboarding";
import Auth from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";
import SupportWidget from "@/components/SupportWidget";
import { ChunkErrorBoundary } from "@/components/ChunkErrorBoundary";

// Everything below the public entry points is code-split so first paint stays light.
const Analyzing = lazy(() => import("@/pages/Analyzing"));
const AnalysisView = lazy(() => import("@/pages/AnalysisView"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Reports = lazy(() => import("@/pages/Reports"));
const Monitoring = lazy(() => import("@/pages/Monitoring"));
const Account = lazy(() => import("@/pages/Account"));
const Admin = lazy(() => import("@/pages/Admin"));
const GrowthOps = lazy(() => import("@/pages/GrowthOps"));
const ProfitBrain = lazy(() => import("@/pages/ProfitBrain"));
const CommandCenter = lazy(() => import("@/pages/CommandCenter"));
const ContentEngine = lazy(() => import("@/pages/ContentEngine"));
const SeoPages = lazy(() => import("@/pages/SeoPages"));
const SeoPage = lazy(() => import("@/pages/SeoPage"));
const Insights = lazy(() => import("@/pages/Insights"));
const CompareMe = lazy(() => import("@/pages/CompareMe"));
const GrowthOS = lazy(() => import("@/pages/GrowthOS"));
const AriaOperations = lazy(() => import("@/pages/AriaOperations"));
const Feedback = lazy(() => import("@/pages/Feedback"));
const SharedReport = lazy(() => import("@/pages/SharedReport"));
const Boardroom = lazy(() => import("@/pages/Boardroom"));
const Autonomy = lazy(() => import("@/pages/Autonomy"));
const Signals = lazy(() => import("@/pages/Signals"));
const Support = lazy(() => import("@/pages/Support"));
const SupportTickets = lazy(() => import("@/pages/SupportTickets"));
const Benchmark = lazy(() => import("@/pages/Benchmark"));
const AdminTestimonials = lazy(() => import("@/pages/AdminTestimonials"));
const AdminUsers = lazy(() => import("@/pages/AdminUsers"));
const AdminUserDetail = lazy(() => import("@/pages/AdminUserDetail"));
const AdminFeedback = lazy(() => import("@/pages/AdminFeedback"));
const AdminMarketing = lazy(() => import("@/pages/AdminMarketing"));
const ProspectRadar = lazy(() => import("@/pages/ProspectRadar"));
const BusinessXray = lazy(() => import("@/pages/BusinessXray"));
const SalesWorkspace = lazy(() => import("@/pages/SalesWorkspace"));
const SalesCatalogue = lazy(() => import("@/pages/SalesCatalogue"));
const ProposalEditor = lazy(() => import("@/pages/ProposalEditor"));
const SharedProposal = lazy(() => import("@/pages/SharedProposal"));
const AriaVoice = lazy(() => import("@/pages/AriaVoice"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("@/pages/PaymentCancel"));

function Protected({ children, adminOnly }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex min-h-screen items-center justify-center"><span className="mono-label">Loading…</span></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && !user.is_admin) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <ChunkErrorBoundary>
    <Suspense fallback={<div className="flex min-h-screen items-center justify-center"><span className="mono-label">Loading…</span></div>}>
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/analyze" element={<Onboarding />} />
      <Route path="/analyzing/:id" element={<Analyzing />} />
      <Route path="/analysis/:id" element={<AnalysisView />} />
      <Route path="/growth-os/:analysisId" element={<GrowthOS />} />
      <Route path="/operations" element={<Protected><AriaOperations /></Protected>} />
      <Route path="/insights" element={<Insights />} />
      <Route path="/insights/:slug" element={<SeoPage />} />
      <Route path="/compare" element={<CompareMe />} />
      <Route path="/feedback" element={<Feedback />} />
      <Route path="/support" element={<Support />} />
      <Route path="/shared/:token" element={<SharedReport />} />
      <Route path="/boardroom" element={<Protected><Boardroom /></Protected>} />
      <Route path="/signals" element={<Protected><Signals /></Protected>} />
      <Route path="/autonomy" element={<Protected><Autonomy /></Protected>} />
      <Route path="/login" element={<Auth mode="login" />} />
      <Route path="/register" element={<Auth mode="register" />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/reports" element={<Protected><Reports /></Protected>} />
      <Route path="/monitoring" element={<Protected><Monitoring /></Protected>} />
      <Route path="/account" element={<Protected><Account /></Protected>} />
      <Route path="/admin" element={<Protected adminOnly><Admin /></Protected>} />
      <Route path="/admin/growth" element={<Protected adminOnly><GrowthOps /></Protected>} />
      <Route path="/admin/ops" element={<Protected adminOnly><CommandCenter /></Protected>} />
      <Route path="/admin/profit" element={<Protected adminOnly><ProfitBrain /></Protected>} />
      <Route path="/admin/content" element={<Protected adminOnly><ContentEngine /></Protected>} />
      <Route path="/admin/seo" element={<Protected adminOnly><SeoPages /></Protected>} />
      <Route path="/admin/support" element={<Protected adminOnly><SupportTickets /></Protected>} />
      <Route path="/admin/benchmark" element={<Protected adminOnly><Benchmark /></Protected>} />
      <Route path="/admin/testimonials" element={<Protected adminOnly><AdminTestimonials /></Protected>} />
      <Route path="/admin/users" element={<Protected adminOnly><AdminUsers /></Protected>} />
      <Route path="/admin/users/:userId" element={<Protected adminOnly><AdminUserDetail /></Protected>} />
      <Route path="/admin/feedback" element={<Protected adminOnly><AdminFeedback /></Protected>} />
      <Route path="/admin/marketing" element={<Protected adminOnly><AdminMarketing /></Protected>} />
      <Route path="/radar" element={<Protected adminOnly><ProspectRadar /></Protected>} />
      <Route path="/radar/xray/:prospectId" element={<Protected adminOnly><BusinessXray /></Protected>} />
      <Route path="/report/opportunity/:token" element={<BusinessXray />} />
      <Route path="/sales" element={<Protected adminOnly><SalesWorkspace /></Protected>} />
      <Route path="/sales/catalogue" element={<Protected adminOnly><SalesCatalogue /></Protected>} />
      <Route path="/sales/proposal/:proposalId" element={<Protected adminOnly><ProposalEditor /></Protected>} />
      <Route path="/proposal/:token" element={<SharedProposal />} />
      <Route path="/voice" element={<Protected adminOnly><AriaVoice /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
    </ChunkErrorBoundary>
  );
}

function App() {
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
            <SupportWidget />
            <Toaster position="top-right" />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
